import ScheduledMessageEditorPopup from "@/extension/context/content/components/add-scheduled-message-button/scheduled-message-editor-popup";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import CalendarView from "@common-views/calendar/calendar-view";
import { CalendarGridV2 } from '@common/views/calendar/grid/calendar-grid';
import { isCalendarV2Enabled } from '@common/views/calendar/grid/calendar-v2-flag';
import MobileAgenda from "@common-views/calendar/mobile-agenda";
import { useIsMobile } from "@common/hooks/use-is-mobile";
import AppView from "@common/components/app-view";
import ClearSchedulePanel from "@common/components/clear-schedule-panel/clear-schedule-panel";
import { addScheduledMessageButtonActions } from "@common/reducers/AddScheduledMessageButtonReducer";
import { isExtensionContext } from "@common/services/detect-context";
import { waQueryContactsAndChatsTool } from "@common/tools/whatsapp";
import { useEffect, useState } from "react";
import AgendaView from "../agenda-view/agenda-view";
import QrModal from "../qr-view/qr-modal";
import { WelcomePopup } from "@common-components/welcome-popup";
import { isClearFlow } from "@common/utils/clear-flow";
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";
import CampaignInternalHeader from "@extension/context/content/components/bt-bulk-dashboard/campaign-internal-header";
import { mainSliceActions } from "@common/reducers/MainReducer";
import { GuideVideoButton } from '@/common/components/guide-video/guide-video-button';

interface CalendarViewContainerProps {
  /** Suppress normal calendar onboarding effects for the /clear-calendar route. */
  bare?: boolean;
}

const CalendarViewContainer = ({ bare = false }: CalendarViewContainerProps = {}) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const calendarMode = useAppSelector((state) => state.CalendarReducer.calendarMode);
  const isAddScheduledMessageModalOpen = useAppSelector((state) => state.AddScheduledMessageButtonReducer.open);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);

  useEffect(() => {
    console.log('[login-flow] CalendarViewContainer: mount');
    try {
      waQueryContactsAndChatsTool({})
    } catch (e) {
      console.error("[waQueryContactsAndChatsTool] error", e)
    }
  }, [dispatch]);

  // Check for welcome query parameter on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('welcome') === 'true') {
      // The regular product onboards on its own route now; only the clear flow keeps the popup.
      if (!isClearFlow() && !isExtensionContext()) {
        window.location.replace('/onboarding?from=welcome_link');
        return;
      }
      setShowWelcomePopup(true);
      // Remove the query parameter from URL without page refresh
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('welcome');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, []);

  let activeExtensionView = ""
  if (isExtensionContext()) {
    activeExtensionView = "calendar"
  }

  // Log render outside of JSX
  console.log('[login-flow] CalendarViewContainer: render');

  const { t } = useTranslation(namespace);

  // Handler for back button in extension context
  const handleBackClick = () => {
    dispatch(mainSliceActions.toggleCalendarView(false));
  };



  return (
    <>
      <AppView
        title={t('calendar.title')}
        activeExtensionView={activeExtensionView}
        showHeader={!isExtensionContext()}
        titleAdornment={<GuideVideoButton lesson="message-planner" variant={isMobile ? 'icon' : 'pill'} surface="planner_header" style={{ marginInlineStart: 4 }} />}
      >

        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {isExtensionContext() && (
            <CampaignInternalHeader
              title={t('calendar.title', 'Message Planner')}
              onBackClick={handleBackClick}
              isMessagesPlanner={true}
            />
          )}
          <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex' }}>
            {/* The clear flow hides BoardsPanel, which is where the scheduled-message
                search + list normally live. Put them back beside the calendar, as a
                panel that collapses to a rail. Desktop only — MobileAgenda already is
                the list. */}
            {bare && !isMobile && <ClearSchedulePanel />}
            <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
              {/* Dedicated mobile Planner (first-class, not a squeezed desktop calendar):
                  a full-width mobile agenda that reuses the same selector + edit thunk. */}
              {isMobile ? (
                <MobileAgenda />
              ) : calendarMode === 'calendar' ? (
                // BT_CALENDAR_V2 — the in-house grid. Opt in with ?calendar=v2,
                // back out with ?calendar=v1, so both can be compared on the same data.
                isCalendarV2Enabled() ? <CalendarGridV2 /> : <CalendarView />
              ) : (
                <AgendaView />
              )}
            </div>
          </div>
        </div>
      </AppView>
      {/* In extension context, dialog is rendered via scheduled-message-dialog.injection.tsx */}
      {isAddScheduledMessageModalOpen && !isExtensionContext() && <ScheduledMessageEditorPopup />}
      <QrModal />
      <WelcomePopup
        isOpen={showWelcomePopup}
        onClose={() => setShowWelcomePopup(false)}
      />
    </>
  );
};

export default CalendarViewContainer;
